import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/apiClient';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    apiClient.get(`/auth/reset-password/${token}`)
      .then(res => { setValid(true); setTokenInfo(res.data); })
      .catch(() => setValid(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      await apiClient.post(`/auth/reset-password/${token}`, { password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally { setLoading(false); }
  };

  if (valid === null) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.spinner}>⟳</div>
          <p style={{ color: '#64748b' }}>Validating reset link…</p>
        </div>
      </div>
    );
  }

  if (valid === false) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.icon}>❌</div>
          <h2 style={styles.title}>Invalid or Expired Link</h2>
          <p style={styles.desc}>This password reset link is invalid or has already been used.</p>
          <Link to="/forgot-password" style={styles.btn2}>Request a New Link</Link>
          <div style={styles.footer}>
            <Link to="/login" style={styles.backLink}>← Back to Login</Link>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.icon}>✅</div>
          <h2 style={styles.title}>Password Reset!</h2>
          <p style={styles.desc}>Your password has been updated. Redirecting to login in 3 seconds…</p>
          <Link to="/login" style={styles.btn}>Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.icon}>🔒</div>
        <h2 style={styles.title}>Reset Password</h2>
        {tokenInfo?.email && (
          <p style={styles.desc}>Resetting password for <strong>{tokenInfo.email}</strong></p>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>New Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Min 8 characters"
              style={styles.input}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirm Password</label>
            <input
              type="password"
              required
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat password"
              style={styles.input}
            />
          </div>
          <button type="submit" disabled={loading} style={styles.btn}>
            {loading ? 'Saving…' : 'Set New Password'}
          </button>
        </form>

        <div style={styles.footer}>
          <Link to="/login" style={styles.backLink}>← Back to Login</Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  card: {
    background: '#fff',
    borderRadius: '1rem',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    textAlign: 'center',
  },
  icon: { fontSize: '2.5rem', marginBottom: '0.75rem' },
  spinner: { fontSize: '2rem', animation: 'spin 1s linear infinite', display: 'inline-block' },
  title: { margin: '0 0 0.75rem 0', fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' },
  desc: { margin: '0 0 1.25rem 0', color: '#64748b', fontSize: '0.9rem' },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.625rem 0.875rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
    textAlign: 'left',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '0.875rem', textAlign: 'left' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151' },
  input: {
    padding: '0.625rem 0.875rem',
    border: '1.5px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  btn: {
    display: 'block',
    padding: '0.75rem',
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
    textAlign: 'center',
    marginTop: '0.25rem',
  },
  btn2: {
    display: 'inline-block',
    padding: '0.625rem 1.25rem',
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  footer: { marginTop: '1.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '1rem' },
  backLink: { color: '#6366f1', textDecoration: 'none', fontSize: '0.875rem', fontWeight: '500' },
};
